<template>
  <q-expansion-item
    ref="refExpansionQuestionary"
    v-model="isExpanded"
    :header-class="
      $route.name === 'pergunta'
        ? 'bg-primary-pure/10 !h-56'
        : '!h-56'
    "
    @click="$emit('click:questionario')"
  >
    <template #header>
      <div class="flex items-center text-white flex-nowrap">
        <q-item-section avatar class="min-w-32 pl-10">
          <q-icon
            class="size-24"
            :class="state.hover ? 'opacity-70' : 'opacity-60'"
            name="svguse:#icon_question"
          />
        </q-item-section>
        <q-item
          class="!text-headline-2 !px-0 self-baseline Nv0-text relative"
        >
          {{ t('questionnaire') }}
        </q-item>
      </div>
      <div class="flex-1" />
      <span
        v-if="$route.name === 'pergunta'"
        class="h-full w-2 bg-primary-pure left-0 top-0 grid z-[2] absolute"
      />
      <q-tooltip v-if="!state?.hover" v-bind="tooltipPropsSidebar">
        {{ t('questionnaire') }}
      </q-tooltip>
    </template>

    <BaseLoader v-if="loading.menu" />

    <q-card v-else class="bg-white/5 overflow-x-hidden">
      <template
        v-for="categoria in menu.categoria_sidebar"
        :key="categoria.id"
      >
        <q-expansion-item
          ref="refExpansionCategory"
          class="border-b border-white/5 Nv0-text"
        >
          <template #header>
            <div
              class="w-full flex items-center justify-between flex-nowrap select-none"
            >
              <h3
                :title="categoria.nome_categoria"
                class="line-clamp-1 text-ellipsis text-caps-1 !font-normal text-neutral-40 self-center"
              >
                {{ categoria.nome_categoria }}
              </h3>

              <div
                class="h-auto bg-white/5 rounded-lg px-8 flex items-center flex-nowrap text-caps-2"
              >
                <span class="text-white mr-2">
                  {{ categoria.perguntas_respondidas }}
                </span>
                /
                <span class="text-neutral-50 ml-2">
                  {{ categoria.total_perguntas }}
                </span>
                <IconCheckProgress
                  v-if="
                    categoria.perguntas_respondidas ===
                    categoria.total_perguntas
                  "
                  class="size-10 text-alert-success ml-8"
                />
              </div>
            </div>
          </template>
          <template
            v-for="perguntaItem in categoria.perguntas"
            :key="perguntaItem.pergunta_id"
          >
            <q-item
              :title="perguntaItem.pergunta"
              :active="sameItem(perguntaItem)"
              active-class="bg-white/5 text-white"
              :to="{
                name: 'pergunta',
                params: { id: perguntaItem.pergunta_id },
              }"
              :style="{
                '--color-status': colorStatus(
                  perguntaItem.status_pergunta
                ),
              }"
              class="flex items-center justify-between flex-nowrap Nv0-text hover:text-white !pl-48 relative"
              @click="closeMenu"
            >
              <span
                v-show="true"
                class="absolute w-2 h-full left-[1.70rem] top-0 !bg-[var(--color-status)] grid place-items-center"
              >
                <span
                  class="!bg-[var(--color-status)] size-8 block rounded-full m-auto relative right-[.1875rem]"
                  :class="{ 'bg-white': sameItem(perguntaItem) }"
                />
              </span>
              <span
                class="!bg-white/10 size-8 block rounded-full m-auto absolute left-[1.5rem] top-1/2 -translate-y-1/2"
                :class="{ 'bg-white': sameItem(perguntaItem) }"
              />
              <p
                :class="sameItem(perguntaItem) ? 'text-white' : ''"
                class="line-clamp-1 text-ellipsis !text-paragraph-3 text-white/70"
              >
                {{ perguntaItem.pergunta }}
              </p>
              <div class="flex items-center gap-4 flex-nowrap">
                <OBadgeStatus
                  v-if="perguntaItem.tag_id"
                  :color="perguntaItem.tag_cor"
                  square
                  size="md"
                  class="h-24"
                >
                  <template #content>
                    <p
                      class="text-center mx-auto"
                      :style="{ color: perguntaItem.tag_cor_texto }"
                    >
                      {{ perguntaItem.tag_nome }}
                    </p>
                  </template>
                </OBadgeStatus>

                <q-icon
                  v-show="!sameItem(perguntaItem)"
                  class="size-24"
                  name="chevron_right"
                />
              </div>
            </q-item>
          </template>
        </q-expansion-item>
      </template>
    </q-card>
  </q-expansion-item>
</template>

<script setup>
import { nextTick, ref, watch } from 'vue'
import { tooltipPropsSidebar } from 'js/utils/GLOBAL'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import BaseLoader from 'components/Loader/BaseLoader.vue'
import IconCheckProgress from 'public/icons/check_progress.svg'
import OBadgeStatus from 'components/Badge/OBadgeStatus.vue'
import usePerguntas from 'composables/usePerguntas'

const route = useRoute()
const { t } = useI18n()
const { pergunta } = usePerguntas()

/** @typedef {import('../../types/menu').MenuTypes} MenuTypes */

/** @type {{menu:MenuTypes}}  */
const { menu, state, loading } = defineProps({
  menu: Object,
  state: Object,
  loading: Object,
  closeMenu: Function,
})

const isExpanded = ref(false)
const refExpansionCategory = ref([])
const refExpansionQuestionary = ref(null)

const sameItem = (pergunta) =>
  +route.params.id === pergunta.pergunta_id

const colorStatus = (status) => {
  if (status === 'pendente') return '#EDA927'
  if (status === 'finalizado') return '#1F9968'
  return '#39393b'
}

watch(
  () => state.collapsed,
  (v) => {
    if (v) isExpanded.value = false
  },
  { immediate: true }
)

watch(
  () => [
    state.hover,
    state.open,
    menu.categoria_sidebar,
    pergunta.value,
  ],

  ([hover, open, categorias, pergunta]) => {
    if (!route.params.id) return

    if (hover || open) {
      const indexItem = categorias?.findIndex(
        (item) => item.id === pergunta?.categoria
      )

      if (indexItem === -1) return

      refExpansionQuestionary.value?.show()
      refExpansionCategory.value?.forEach((element) =>
        element?.hide()
      )

      nextTick(() => {
        refExpansionCategory.value[indexItem]?.show()
      })
    }
  }
)

const emits = defineEmits(['click:questionario'])
</script>

<style lang="scss" scoped></style>
